import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { clearAuthStorage } from "@/utils";
import apiClient from "@/services/apiClient";

/**
 * React Query-backed replacement for usePostData.
 * Caches POST responses by [url, requestBody] key.
 *
 * Returns [data, isLoaded, error, refetch, isFetching].
 *   - isLoaded: false only on the very first fetch (no cached data yet). Safe to
 *     gate loading spinners on this — background refetches leave it true.
 *   - isFetching: true whenever any network request is in-flight, including
 *     background refetches. Use this for subtle "refreshing" indicators only.
 */
const usePostQuery = (url, requestBody, options = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryFn = async () => {
    try {
      const response = await apiClient.post(url, requestBody, {
        headers: options.headers,
      });
      const response_json = response.data;
      return response_json ? response_json["data"] : undefined;
    } catch (err) {
      if (err.response?.status === 401) {
        clearAuthStorage();
        localStorage.setItem(
          "redirectAfterLogin",
          location.pathname + location.search,
        );
        navigate("/login");
        return null;
      }
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        `HTTP error! Status: ${err.response?.status}`;
      throw new Error(errorMessage);
    }
  };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [url, requestBody],
    queryFn,
    enabled: !options.skip && !!url && requestBody != null,
    staleTime: options.staleTime ?? 5 * 60 * 1000, // default: 5 minutes
  });

  const isLoaded = !isLoading && data !== undefined;

  return [data ?? [], isLoaded, error ?? null, refetch, isFetching];
};

export default usePostQuery;
